/**
 * Settings Class
 *
 * Manages puzzle type settings loaded from XML configuration.
 * Migrated from the legacy Beallitas constructor function.
 */

/**
 * Settings configuration interface
 */
export interface SettingsConfig {
    type: string;
    subtype: string;
    borderColor: string;
    coloredBase64?: string;
    bridgeBase64?: string;
    // Allow flags from type-specific settings
    allowColored?: boolean; // megfejtés - colored cells for solution letters
    allowSolution?: boolean; // megoldás - solution input
    allowBridge?: boolean; // híd - bridge cells
    allowEmpty?: boolean; // üres - empty cells
    allowBlack?: boolean; // fekete - black cells
    allowImages?: boolean; // ábra - image cells
}

/**
 * Settings class for puzzle type configuration.
 */
export class Settings {
    private _type: string;
    private _subtype: string;
    private _borderColor: string;
    private _coloredBase64: string;
    private _bridgeBase64: string;

    // Allow flags - control which cell types are available
    private _allowColored: boolean;
    private _allowSolution: boolean;
    private _allowBridge: boolean;
    private _allowEmpty: boolean;
    private _allowBlack: boolean;
    private _allowImages: boolean;

    constructor(config: SettingsConfig) {
        this._type = config.type;
        this._subtype = config.subtype;
        this._borderColor = config.borderColor;
        this._coloredBase64 = config.coloredBase64 ?? '';
        this._bridgeBase64 = config.bridgeBase64 ?? '';

        // Allow flags default to true (most permissive)
        this._allowColored = config.allowColored ?? true;
        this._allowSolution = config.allowSolution ?? true;
        this._allowBridge = config.allowBridge ?? true;
        this._allowEmpty = config.allowEmpty ?? true;
        this._allowBlack = config.allowBlack ?? true;
        this._allowImages = config.allowImages ?? true;
    }

    // ============================================================================
    // Getters
    // ============================================================================

    get type(): string {
        return this._type;
    }

    get subtype(): string {
        return this._subtype;
    }

    get borderColor(): string {
        return this._borderColor;
    }

    get coloredBase64(): string {
        return this._coloredBase64;
    }

    get bridgeBase64(): string {
        return this._bridgeBase64;
    }

    // ============================================================================
    // Setters
    // ============================================================================

    set type(value: string) {
        this._type = value;
    }

    set subtype(value: string) {
        this._subtype = value;
    }

    set borderColor(value: string) {
        this._borderColor = value;
    }

    set coloredBase64(value: string) {
        this._coloredBase64 = value;
    }

    set bridgeBase64(value: string) {
        this._bridgeBase64 = value;
    }

    // ============================================================================
    // Allow Flag Getters (Legacy compatibility: allowMegfejtes, allowHid, etc.)
    // ============================================================================

    /** Check if colored cells are allowed (megfejtés) */
    get allowColored(): boolean {
        return this._allowColored;
    }

    /** Check if solution input is allowed (megoldás) */
    get allowSolution(): boolean {
        return this._allowSolution;
    }

    /** Check if bridge cells are allowed (híd) */
    get allowBridge(): boolean {
        return this._allowBridge;
    }

    /** Check if empty cells are allowed (üres) */
    get allowEmpty(): boolean {
        return this._allowEmpty;
    }

    /** Check if black cells are allowed (fekete) */
    get allowBlack(): boolean {
        return this._allowBlack;
    }

    /** Check if image cells are allowed (ábra) */
    get allowImages(): boolean {
        return this._allowImages;
    }

    // ============================================================================
    // Allow Flag Setters
    // ============================================================================

    set allowColored(value: boolean) {
        this._allowColored = value;
    }

    set allowSolution(value: boolean) {
        this._allowSolution = value;
    }

    set allowBridge(value: boolean) {
        this._allowBridge = value;
    }

    set allowEmpty(value: boolean) {
        this._allowEmpty = value;
    }

    set allowBlack(value: boolean) {
        this._allowBlack = value;
    }

    set allowImages(value: boolean) {
        this._allowImages = value;
    }

    // ============================================================================
    // Serialization
    // ============================================================================

    toXml(): string {
        return `<beállítás típus="${this._type}" altípus="${this._subtype}" keretszín="${this._borderColor}"/>`;
    }

    static fromXml(element: Element): Settings {
        return new Settings({
            type: element.getAttribute('típus') ?? '',
            subtype: element.getAttribute('altípus') ?? '',
            borderColor: element.getAttribute('keretszín') ?? '',
        });
    }

    /**
     * Parse allow flags from settings XML.
     * The XML contains elements named by puzzle type with attributes like:
     * <olaszmodra megfejtés="1" híd="0" üres="1" fekete="1" ábra="1"/>
     *
     * @param typeName - The puzzle type name (e.g., 'olaszmodra', 'sudoku')
     * @param xmlString - The settings XML from /beallitasok endpoint
     */
    loadFromSettingsXml(typeName: string, xmlString: string): void {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlString, 'application/xml');

        const elements = xmlDoc.getElementsByTagName(typeName);
        if (elements.length > 0) {
            const element = elements[0];

            // Parse boolean attributes ("1" = true, anything else = false)
            const getBoolean = (attr: string | null): boolean => attr === '1';

            this._allowColored = getBoolean(element.getAttribute('megfejtés'));
            this._allowSolution = getBoolean(element.getAttribute('megoldás'));
            this._allowBridge = getBoolean(element.getAttribute('híd'));
            this._allowEmpty = getBoolean(element.getAttribute('üres'));
            this._allowBlack = getBoolean(element.getAttribute('fekete'));
            this._allowImages = getBoolean(element.getAttribute('ábra'));
        }
    }

    /**
     * Fetch settings from the server and parse allow flags.
     *
     * @param puzzleId - The puzzle ID
     * @param typeName - The puzzle type name
     * @param isBuilderMode - Whether in builder mode (fejtoMod=1 if not)
     */
    async fetchAndLoad(
        puzzleId: number,
        typeName: string,
        isBuilderMode: boolean,
    ): Promise<void> {
        const param = isBuilderMode ? '' : '&fejtoMod=1';
        const response = await fetch(
            `/beallitasok?rejtvenyID=${puzzleId}${param}`,
        );
        const xmlString = await response.text();
        this.loadFromSettingsXml(typeName, xmlString);
    }

    /**
     * Fetch settings synchronously (for legacy compatibility).
     *
     * @param puzzleId - The puzzle ID
     * @param typeName - The puzzle type name
     * @param isBuilderMode - Whether in builder mode
     */
    fetchAndLoadSync(
        puzzleId: number,
        typeName: string,
        isBuilderMode: boolean,
    ): void {
        const param = isBuilderMode ? '' : '&fejtoMod=1';
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `/beallitasok?rejtvenyID=${puzzleId}${param}`, false);
        xhr.send();
        this.loadFromSettingsXml(typeName, xhr.responseText);
    }
}
